import {
  motion,
  usePresence,
  useReducedMotion,
  type Variants
} from 'motion/react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { cn } from 'v5/platform/dom/cn';
import { revealVertically } from 'v5/platform/dom/revealVertically';
import { useUnitTransition } from './unitMotion';

export interface ListRowMotionProps {
  /** The list's gap between rows as a CSS length (`1rem` for `gap-y-4`). */
  gap: string;
  /**
   * Grows in when mounted; `false` for rows there when the list first
   * renders (`useNewRowsAppear` in `unitMotion`).
   */
  appear?: boolean;
  /**
   * `false` collapses the row while it stays mounted, then calls `onGone`:
   * for rows that must not outlive their data (react-hook-form array items,
   * whose paths shift on removal). Rows in `AnimatePresence` just unmount.
   */
  present?: boolean;
  onGone?(): void;
  /** Scrolls the row into view once it has grown in: a new editor. */
  reveal?: boolean;
  children: ReactNode;
}

/**
 * One list row entering or leaving: it grows from nothing and collapses away,
 * the gap before it too, so the rows and buttons below slide instead of
 * jumping.
 *
 * It clips only while it moves, with `overflow: clip`, which unlike `hidden`
 * cannot be scrolled — focusing a field inside does not shift the row's
 * content. Room around it, taken back by negative margins, keeps what
 * reaches past the row in view meanwhile: a unit's frame (0.5rem,
 * `UnitLayout`), a list row's band (0.5rem) and shadow.
 * The room is padding of an inner box: motion measures `height: auto`
 * without the animated box's own padding, which would end the animation
 * with a jump.
 */
export function ListRowMotion({
  gap,
  appear = true,
  present = true,
  onGone,
  reveal = false,
  children
}: ListRowMotionProps) {
  const room = useRef<HTMLDivElement>(null);
  const transition = useUnitTransition();
  const reducedMotion = useReducedMotion();
  const [inPresence, safeToRemove] = usePresence();
  const wanted = present && inPresence;
  // The row follows `wanted` a frame late. Mounting an editor blocks the main
  // thread, and a growth started with the mount would first show up a third
  // of the way through; rows entering and leaving together (a saved row
  // replacing its editor, the empty label giving way) start together.
  const [shown, setShown] = useState(!appear);
  // Grown in and done moving. Kept in React state, not in motion's
  // `transitionEnd`: motion stores that value without rendering it, so a row
  // nothing re-renders afterwards (a freshly saved one) would stay clipped.
  const [settled, setSettled] = useState(!appear);
  useEffect(() => {
    if (shown === wanted) return;
    const frame = requestAnimationFrame(() => {
      setSettled(false);
      setShown(wanted);
    });
    return () => cancelAnimationFrame(frame);
  }, [shown, wanted]);

  const hidden = {
    height: 0,
    opacity: 0,
    marginTop: `-${gap}`,
    marginBottom: '0rem'
  };
  const variants: Variants = {
    hidden,
    // Same as `hidden`; its own name, so that a row leaving before it ever
    // showed still completes an animation, and is let go.
    gone: hidden,
    shown: {
      height: 'auto',
      opacity: 1,
      // Takes back the room above (`pt-2`).
      marginTop: '-0.5rem',
      marginBottom: `-${gap}`
    }
  };
  return (
    <motion.div
      variants={variants}
      initial={appear ? 'hidden' : false}
      animate={shown ? 'shown' : wanted ? 'hidden' : 'gone'}
      transition={transition}
      className={cn('-mx-3', !(shown && settled) && 'overflow-clip')}
      onAnimationComplete={definition => {
        if (definition === 'gone') {
          if (!inPresence) safeToRemove?.();
          else onGone?.();
          return;
        }
        if (definition !== 'shown') return;
        setSettled(true);
        const card = room.current?.firstElementChild;
        if (reveal && card) {
          revealVertically(card, reducedMotion ? 'auto' : 'smooth');
        }
      }}
    >
      <div
        ref={room}
        className="flow-root px-3 pt-2"
        style={{ paddingBottom: gap }}
      >
        {children}
      </div>
    </motion.div>
  );
}
